import { useState, useEffect, useRef } from 'react';
import { aquaApi } from '../aquaApi';
import { fmtDate } from '../dateUtils';
import DateInput from '../DateInput';

const EMPTY = {
  cropCode: '', startDate: '', endDate: '',
  totalAcres: '', numPonds: '', seedCount: '', seedRate: '',
  status: 'ACTIVE', notes: ''
};

function isBlank(v) { return v === '' || v === null || v === undefined; }

export default function AquaCrops({ onSelect, onCropChange, role = 'admin' }) {
  const canWrite = role !== 'readonly';
  const canDelete = role === 'admin';
  const [crops, setCrops] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState('list');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Ponds tab state
  const EMPTY_POND = { pondName: '', area: '', description: '' };
  const [pondCropId, setPondCropId]   = useState('');
  const [ponds, setPonds]             = useState([]);
  const [pondForm, setPondForm]       = useState({ ...EMPTY_POND });
  const [pondEditId, setPondEditId]   = useState(null);
  const [pondLoading, setPondLoading] = useState(false);
  const [pondSaving, setPondSaving]   = useState(false);
  const [pondErr, setPondErr]         = useState('');

  // Release Dates tab state
  const [relCropId, setRelCropId]     = useState('');
  const [relPondList, setRelPondList] = useState([]);   // pond records from chmr_ponds
  const [relData, setRelData]         = useState({});   // { pondName: { date, seedCount, seedRate, supplier, notes } }
  const [relSelectedPond, setRelSelectedPond] = useState('');
  const [relRows, setRelRows]         = useState([]);
  const [relLoading, setRelLoading]   = useState(false);
  const [relSaving, setRelSaving]     = useState(false);
  const [relErr, setRelErr]           = useState('');


  // Refs for focusing invalid fields on Cancel
  const cropCodeRef   = useRef(null);
  const endDateRef    = useRef(null); // ref on the DateInput wrapper div
  const totalAcresRef = useRef(null);
  const numPondsRef   = useRef(null);
  const seedCountRef  = useRef(null);
  const seedRateRef   = useRef(null);

  const load = () => aquaApi.getCrops().then(d => setCrops(Array.isArray(d) ? d.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)) : d)).finally(() => setLoading(false));
  useEffect(() => { load(); loadPonds(); }, []);

  // Auto-select latest crop in Pond Stocking tab when crops first load
  useEffect(() => {
    if (crops.length > 0 && !relCropId) {
      const latest = [...crops].sort((a, b) => new Date(b.start_date) - new Date(a.start_date))[0];
      const id = String(latest.id);
      setRelCropId(id);
      loadRelease(id);
    }
  }, [crops]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Duplicate check: same crop code exists in another record
  const isDuplicateCode = form.cropCode.trim() !== '' &&
    crops.some(c => c.crop_code.trim().toLowerCase() === form.cropCode.trim().toLowerCase() && c.id !== editId);

  // Minimum end date = start date + 60 days
  const minEndDate = (() => {
    if (!form.startDate) return '';
    const d = new Date(form.startDate);
    d.setDate(d.getDate() + 60);
    return d.toISOString().split('T')[0];
  })();

  const endDateTooEarly = form.startDate && form.endDate && form.endDate < minEndDate;
  const endDateSameAsStart = form.startDate && form.endDate && form.endDate === form.startDate;

  // Ponds: must be a whole number >= 1
  const pondsVal = form.numPonds === '' ? null : Number(form.numPonds);
  const pondsHasDecimal = form.numPonds !== '' && String(form.numPonds).includes('.');
  const pondsIsZeroOrNeg = pondsVal !== null && pondsVal < 1;
  const pondsError = pondsHasDecimal
    ? 'Ponds must be a whole number — no decimals.'
    : pondsIsZeroOrNeg
      ? 'Minimum 1 pond required.'
      : '';

  // Acres: decimal OK but must be > 0
  const acresVal = form.totalAcres === '' ? null : parseFloat(form.totalAcres);
  const acresIsZeroOrNeg = acresVal !== null && acresVal <= 0;
  const acresError = acresIsZeroOrNeg ? 'Total acres must be greater than zero (e.g. 2.5, 3.82).' : '';

  // Seed Count: whole number > 0
  const seedCountVal = form.seedCount === '' ? null : Number(form.seedCount);
  const seedCountHasDecimal = form.seedCount !== '' && String(form.seedCount).includes('.');
  const seedCountIsZeroOrNeg = seedCountVal !== null && seedCountVal <= 0;
  const seedCountError = seedCountHasDecimal
    ? 'Seed count must be a whole number — no decimals.'
    : seedCountIsZeroOrNeg
      ? 'Seed count must be greater than zero.'
      : '';

  // Seed Rate: decimal OK but must be > 0
  const seedRateVal = form.seedRate === '' ? null : parseFloat(form.seedRate);
  const seedRateIsZeroOrNeg = seedRateVal !== null && seedRateVal <= 0;
  const seedRateError = seedRateIsZeroOrNeg ? 'Seed rate must be greater than zero (e.g. 0.35).' : '';

  // All mandatory fields filled and no duplicate and date range valid
  const isFormValid =
    !isBlank(form.cropCode) &&
    !isBlank(form.startDate) &&
    !isBlank(form.endDate) &&
    !isBlank(form.totalAcres) &&
    !isBlank(form.numPonds) &&
    !isBlank(form.seedCount) &&
    !isBlank(form.seedRate) &&
    !isDuplicateCode &&
    !endDateTooEarly &&
    !endDateSameAsStart &&
    !pondsError &&
    !acresError &&
    !seedCountError &&
    !seedRateError;

  const handleCancel = () => {
    const hasFieldErrors = isDuplicateCode || endDateTooEarly || endDateSameAsStart ||
                           !!acresError || !!pondsError || !!seedCountError || !!seedRateError;

    if (!hasFieldErrors) {
      setTab('list'); setEditId(null); setForm(EMPTY); setErr('');
      return;
    }

    // Clear only the invalid fields; keep everything else intact
    const updates = {};
    let focusFn = null;

    if (isDuplicateCode) {
      updates.cropCode = '';
      focusFn = focusFn || (() => cropCodeRef.current?.focus());
    }
    if (endDateTooEarly || endDateSameAsStart) {
      updates.endDate = '';
      focusFn = focusFn || (() => endDateRef.current?.querySelector('input[type=text]')?.focus());
    }
    if (acresError) {
      updates.totalAcres = '';
      focusFn = focusFn || (() => totalAcresRef.current?.focus());
    }
    if (pondsError) {
      updates.numPonds = '';
      focusFn = focusFn || (() => numPondsRef.current?.focus());
    }
    if (seedCountError) {
      updates.seedCount = '';
      focusFn = focusFn || (() => seedCountRef.current?.focus());
    }
    if (seedRateError) {
      updates.seedRate = '';
      focusFn = focusFn || (() => seedRateRef.current?.focus());
    }

    setForm(f => ({ ...f, ...updates }));
    setTimeout(() => focusFn?.(), 0);
  };

  const handleSave = async () => {
    if (!isFormValid) return;
    try {
      if (editId) await aquaApi.updateCrop(editId, form);
      else await aquaApi.createCrop(form);
      setForm(EMPTY); setEditId(null); setErr(''); setTab('list'); load();
      if (onCropChange) onCropChange();
    } catch (e) { setErr(e.message); }
  };

  const handleEdit = (c) => {
    setForm({
      cropCode: c.crop_code || '', startDate: c.start_date || '',
      endDate: c.end_date || '', totalAcres: c.total_acres || '',
      numPonds: c.num_ponds || '', seedCount: c.seed_count || '',
      seedRate: c.seed_rate || '', status: c.status || 'ACTIVE', notes: c.notes || ''
    });
    setEditId(c.id); setErr(''); setTab('form');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this crop record?')) return;
    await aquaApi.deleteCrop(id); load();
    if (onCropChange) onCropChange();
  };

  // ── Ponds helpers ─────────────────────────────────────────────────────────────

  const loadPonds = () => {
    setPondLoading(true);
    aquaApi.getPonds()
      .then(d => setPonds(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setPondLoading(false));
  };

  const handlePondSave = async () => {
    if (!pondForm.pondName.trim()) { setPondErr('Pond name is required'); return; }
    setPondSaving(true); setPondErr('');
    try {
      const payload = { pondName: pondForm.pondName.trim(), area: pondForm.area, description: pondForm.description };
      if (pondEditId) await aquaApi.updatePond(pondEditId, payload);
      else            await aquaApi.createPond(payload);
      setPondForm({ ...EMPTY_POND }); setPondEditId(null);
      loadPonds();
    } catch (e) { setPondErr(e.message || 'Save failed — pond name may already exist'); }
    finally { setPondSaving(false); }
  };

  const handlePondEdit = (p) => {
    setPondForm({ pondName: p.pond_name || '', area: p.area != null ? String(p.area) : '', description: p.description || '' });
    setPondEditId(p.id); setPondErr('');
  };

  const handlePondDelete = async (id) => {
    if (!window.confirm('Delete this pond?')) return;
    await aquaApi.deletePond(id); loadPonds();
  };

  // ── Release Dates helpers ─────────────────────────────────────────────────────

  const loadRelease = (cropId) => {
    if (!cropId) { setRelRows([]); setRelData({}); return; }
    setRelLoading(true);
    aquaApi.getCropRelease(cropId)
      .then(rows => {
        const releaseRows = Array.isArray(rows) ? rows : [];
        setRelRows(releaseRows);
        const map = {};
        releaseRows.forEach(r => {
          map[r.pond_no] = {
            date: r.release_date ? r.release_date.split('T')[0] : '',
            seedCount: r.seed_count != null ? String(r.seed_count) : '',
            seedRate: r.seed_rate != null ? String(r.seed_rate) : '',
            supplier: r.supplier || '',
            notes: r.notes || '',
          };
        });
        setRelData(map);
      })
      .catch(() => {})
      .finally(() => setRelLoading(false));
  };

  const handleRelCropChange = (id) => {
    setRelCropId(id); setRelErr(''); setRelSelectedPond('');
    loadRelease(id);
  };

  const handleRelSave = async () => {
    if (!relCropId) { setRelErr('Select a crop first'); return; }
    if (!relSelectedPond) { setRelErr('Select a pond'); return; }
    const pd = relData[relSelectedPond] || {};
    if (!pd.date) { setRelErr('Enter a release date'); return; }
    setRelSaving(true); setRelErr('');
    try {
      await aquaApi.saveCropRelease({ cropId: relCropId, entries: [
        { pondNo: relSelectedPond, releaseDate: pd.date, seedCount: pd.seedCount, seedRate: pd.seedRate, supplier: pd.supplier, notes: pd.notes }
      ]});
      await loadRelease(relCropId);
    } catch (e) { setRelErr(e.message); }
    finally { setRelSaving(false); }
  };

  const handleRelDelete = async (id) => {
    if (!window.confirm('Clear this release date?')) return;
    await aquaApi.deleteCropRelease(id);
    loadRelease(relCropId);
  };

  const statusBadge = (s) => ({
    ACTIVE: { bg: '#e6f4ea', color: '#137333' },
    HARVESTED: { bg: '#e8f0fe', color: '#1a73e8' },
    CLOSED: { bg: '#fce8e6', color: '#c5221f' },
  }[s] || { bg: '#f1f3f4', color: '#5f6368' });

  if (loading) return <div className="loading-msg">Loading…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' active' : ''}`} onClick={() => { setTab('list'); setEditId(null); setForm(EMPTY); setErr(''); }}>🦐 Crop List</button>
        {canWrite && <button className={`tab${tab === 'form' ? ' active' : ''}`} onClick={() => { setTab('form'); if (!editId) { setForm(EMPTY); setErr(''); } }}>{editId ? '✏️ Edit Crop' : '➕ New Crop'}</button>}
        <button className={`tab${tab === 'ponds' ? ' active' : ''}`} onClick={() => setTab('ponds')}>🏊 Ponds</button>
        <button className={`tab${tab === 'release' ? ' active' : ''}`} onClick={() => setTab('release')}>🐟 Pond Stocking</button>
      </div>

      {tab === 'list' && (
        <div>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-icon green" style={{ fontSize: 22 }}>🦐</div>
              <div><div className="stat-label">Total Crops</div><div className="stat-value">{crops.length}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon blue" style={{ fontSize: 22 }}>🟢</div>
              <div><div className="stat-label">Active</div><div className="stat-value">{crops.filter(c => c.status === 'ACTIVE').length}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon orange" style={{ fontSize: 22 }}>🏆</div>
              <div><div className="stat-label">Harvested</div><div className="stat-value">{crops.filter(c => c.status === 'HARVESTED').length}</div></div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><div className="card-title">🦐 Crop Records</div></div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Crop Code</th><th>Start Date</th><th>End Date</th>
                    <th>Acres</th><th>Ponds</th><th>Seed Count</th>
                    <th>Seed Rate</th><th style={{ textAlign: 'right' }}>Seed Cost</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {crops.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No crops yet — add the first one</td></tr>}
                  {crops.map(c => {
                    const seedCost = (parseFloat(c.seed_count || 0) * parseFloat(c.seed_rate || 0));
                    return (
                    <tr key={c.id}>
                      <td><strong>{c.crop_code}</strong></td>
                      <td>{fmtDate(c.start_date)}</td>
                      <td>{fmtDate(c.end_date)}</td>
                      <td>{c.total_acres}</td>
                      <td>{c.num_ponds}</td>
                      <td>{c.seed_count ? Number(c.seed_count).toLocaleString('en-IN') : '—'}</td>
                      <td>{c.seed_rate ? `₹${c.seed_rate}` : '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#137333' }}>
                        {seedCost > 0 ? `₹${seedCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td>
                        <span style={{ ...statusBadge(c.status), padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                          {c.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn-sm btn-primary" onClick={() => onSelect && onSelect(c)} style={{ marginRight: 4 }}>Open</button>
                        {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleEdit(c)} style={{ marginRight: 4 }}>Edit</button>}
                        {canDelete && <button className="btn-sm btn-danger" onClick={() => handleDelete(c.id)}>Del</button>}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'form' && (
        <div className="card">
          <div className="card-header"><div className="card-title">{editId ? '✏️ Edit Crop' : '➕ New Crop'}</div></div>
          <div className="card-body">
            {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}
            <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>Fields marked <span style={{ color: '#c5221f', fontWeight: 700 }}>*</span> are required</div>
            <div className="form-grid cols-3">

              {/* Crop Code */}
              <div className="form-group">
                <label className="form-label">Crop Code <span style={{ color: '#c5221f' }}>*</span></label>
                <input
                  ref={cropCodeRef}
                  className="form-control"
                  placeholder="e.g. CRPN/007"
                  value={form.cropCode}
                  onChange={e => set('cropCode', e.target.value)}
                  style={isDuplicateCode || !form.cropCode.trim() ? {} : {}}
                />
                {isDuplicateCode && (
                  <div style={{ color: '#c5221f', fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                    ⚠️ Crop code already exists — please use a different code.
                  </div>
                )}
              </div>

              {/* Start Date */}
              <div className="form-group">
                <label className="form-label">Start Date <span style={{ color: '#c5221f' }}>*</span></label>
                <DateInput
                  className="form-control"
                  value={form.startDate}
                  onChange={e => set('startDate', e.target.value)}
                  style={form.startDate ? {} : {}}
                />
              </div>

              {/* End Date */}
              <div className="form-group">
                <label className="form-label">End Date <span style={{ color: '#c5221f' }}>*</span></label>
                <div ref={endDateRef}>
                <DateInput
                  className="form-control"
                  value={form.endDate}
                  onChange={e => set('endDate', e.target.value)}
                  min={minEndDate || undefined}
                  style={endDateTooEarly || endDateSameAsStart
                    ? {}
                    : form.endDate ? {} : {}}
                />
                {endDateSameAsStart && (
                  <div style={{ color: '#c5221f', fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                    ⚠️ End date cannot be the same as start date.
                  </div>
                )}
                {endDateTooEarly && !endDateSameAsStart && (
                  <div style={{ color: '#c5221f', fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                    ⚠️ Minimum crop period is 60 days. Earliest end date: <strong>{minEndDate}</strong>
                  </div>
                )}
                {form.startDate && !form.endDate && (
                  <div style={{ color: '#1a73e8', fontSize: 12, marginTop: 4 }}>
                    Minimum end date: <strong>{minEndDate}</strong> (60 days from start)
                  </div>
                )}
                </div>{/* end endDateRef wrapper */}
              </div>

              {/* Total Acres */}
              <div className="form-group">
                <label className="form-label">Total Acres <span style={{ color: '#c5221f' }}>*</span></label>
                <input
                  ref={totalAcresRef}
                  className="form-control" type="number" step="0.01" min="0.01" placeholder="e.g. 3.82"
                  value={form.totalAcres}
                  onChange={e => set('totalAcres', e.target.value)}
                  style={acresError
                    ? {}
                    : form.totalAcres ? {} : {}}
                />
                {acresError && (
                  <div style={{ color: '#c5221f', fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                    ⚠️ {acresError}
                  </div>
                )}
                {!acresError && !form.totalAcres && (
                  <div style={{ color: '#888', fontSize: 11, marginTop: 3 }}>Decimals allowed, e.g. 2.5 or 3.82</div>
                )}
              </div>

              {/* No. of Ponds */}
              <div className="form-group">
                <label className="form-label">No. of Ponds <span style={{ color: '#c5221f' }}>*</span></label>
                <input
                  ref={numPondsRef}
                  className="form-control" type="number" step="1" min="1" placeholder="e.g. 4"
                  value={form.numPonds}
                  onChange={e => set('numPonds', e.target.value.replace('.', ''))}
                  style={pondsError
                    ? {}
                    : form.numPonds ? {} : {}}
                />
                {pondsError && (
                  <div style={{ color: '#c5221f', fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                    ⚠️ {pondsError}
                  </div>
                )}
                {!pondsError && !form.numPonds && (
                  <div style={{ color: '#888', fontSize: 11, marginTop: 3 }}>Whole number only, minimum 1</div>
                )}
              </div>

              {/* Status */}
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option>ACTIVE</option><option>HARVESTED</option><option>CLOSED</option>
                </select>
              </div>

              {/* Seed Count */}
              <div className="form-group">
                <label className="form-label">Seed Count (PCs) <span style={{ color: '#c5221f' }}>*</span></label>
                <input
                  ref={seedCountRef}
                  className="form-control" type="number" step="1" min="1" placeholder="e.g. 450000"
                  value={form.seedCount}
                  onChange={e => set('seedCount', e.target.value.replace('.', ''))}
                  style={seedCountError
                    ? {}
                    : form.seedCount ? {} : {}}
                />
                {seedCountError && (
                  <div style={{ color: '#c5221f', fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                    ⚠️ {seedCountError}
                  </div>
                )}
                {!seedCountError && !form.seedCount && (
                  <div style={{ color: '#888', fontSize: 11, marginTop: 3 }}>Whole number, must be &gt; 0</div>
                )}
              </div>

              {/* Seed Rate */}
              <div className="form-group">
                <label className="form-label">Seed Rate (₹/Pc) <span style={{ color: '#c5221f' }}>*</span></label>
                <input
                  ref={seedRateRef}
                  className="form-control" type="number" step="0.01" min="0.01" placeholder="e.g. 0.35"
                  value={form.seedRate}
                  onChange={e => set('seedRate', e.target.value)}
                  style={seedRateError
                    ? {}
                    : form.seedRate ? {} : {}}
                />
                {seedRateError && (
                  <div style={{ color: '#c5221f', fontSize: 12, marginTop: 4, fontWeight: 600 }}>
                    ⚠️ {seedRateError}
                  </div>
                )}
                {!seedRateError && !form.seedRate && (
                  <div style={{ color: '#888', fontSize: 11, marginTop: 3 }}>Decimals allowed, must be &gt; 0</div>
                )}
              </div>

              {/* Notes — optional */}
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>

            {/* Progress hint when fields are missing or invalid */}
            {!isFormValid && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 10, padding: '6px 10px', background: '#f8f9fa', borderRadius: 6, border: '1px solid #e8eaed' }}>
                {isDuplicateCode
                  ? '⚠️ Change the crop code — it already exists.'
                  : (endDateSameAsStart || endDateTooEarly)
                    ? `⚠️ End date must be at least 60 days after start date (min: ${minEndDate}).`
                    : pondsError
                      ? `⚠️ ${pondsError}`
                      : acresError
                        ? `⚠️ ${acresError}`
                        : seedCountError
                          ? `⚠️ ${seedCountError}`
                          : seedRateError
                            ? `⚠️ ${seedRateError}`
                            : '⚠️ Fill in all required fields to enable Save.'}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!isFormValid}
                title={!isFormValid ? (isDuplicateCode ? 'Crop code already exists' : 'Fill all required fields') : ''}
                style={{ opacity: isFormValid ? 1 : 0.45, cursor: isFormValid ? 'pointer' : 'not-allowed' }}
              >
                {editId ? 'Update Crop' : 'Save Crop'}
              </button>
              <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Ponds tab ────────────────────────────────────────────────────────── */}
      {tab === 'ponds' && (
        <div>
          <div className="aq-pond-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>

              {/* Pond list */}
              <div className="card">
                <div className="card-header">
                  <div className="card-title">🏊 Farm Ponds</div>
                  <span style={{ fontSize: 12, color: '#888' }}>{ponds.length} pond{ponds.length !== 1 ? 's' : ''} — shared across all crops</span>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  {pondLoading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading…</div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Pond Name</th>
                          <th style={{ textAlign: 'right' }}>Area</th>
                          <th>Description</th>
                          {canWrite && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {ponds.length === 0 && (
                          <tr><td colSpan={canWrite ? 5 : 4} style={{ textAlign: 'center', color: '#888', padding: 32 }}>
                            No ponds yet — add one using the form →
                          </td></tr>
                        )}
                        {ponds.map((p, i) => (
                          <tr key={p.id} style={{ background: pondEditId === p.id ? 'rgba(26,115,232,.06)' : undefined }}>
                            <td style={{ color: '#888', fontSize: 12 }}>{i + 1}</td>
                            <td>
                              <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 8,
                                padding: '3px 10px', fontWeight: 700, fontSize: 13 }}>
                                {p.pond_name}
                              </span>
                            </td>
                            <td style={{ textAlign: 'right', color: '#5f6368' }}>
                              {p.area ? `${parseFloat(p.area).toFixed(2)} ac` : '—'}
                            </td>
                            <td style={{ color: '#5f6368', fontSize: 13 }}>{p.description || '—'}</td>
                            {canWrite && (
                              <td>
                                <button className="btn-sm btn-secondary" onClick={() => handlePondEdit(p)} style={{ marginRight: 4 }}>Edit</button>
                                {canDelete && <button className="btn-sm btn-danger" onClick={() => handlePondDelete(p.id)}>Del</button>}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      {ponds.length > 0 && (
                        <tfoot>
                          <tr>
                            <td colSpan={2} style={{ fontWeight: 700, padding: '10px 12px' }}>Total</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>
                              {ponds.some(p => p.area)
                                ? `${ponds.reduce((s, p) => s + parseFloat(p.area || 0), 0).toFixed(2)} ac`
                                : '—'}
                            </td>
                            <td colSpan={canWrite ? 2 : 1}></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  )}
                </div>
              </div>

              {/* Add / Edit form */}
              {canWrite && (
                <div className="card">
                  <div className="card-header">
                    <div className="card-title">{pondEditId ? '✏️ Edit Pond' : '➕ Add Pond'}</div>
                  </div>
                  <div className="card-body">
                    {pondErr && <div className="alert alert-error" style={{ marginBottom: 12, fontSize: 13 }}>{pondErr}</div>}

                    <div className="form-group">
                      <label className="form-label">Pond Name <span style={{ color: '#c5221f' }}>*</span></label>
                      <input className="form-control" placeholder="e.g. P1, Pond-A, North Pond"
                        value={pondForm.pondName}
                        onChange={e => setPondForm(f => ({ ...f, pondName: e.target.value }))}
                        style={!pondForm.pondName.trim() ? {} : {}}
                        onKeyDown={e => e.key === 'Enter' && handlePondSave()} />
                      {!pondForm.pondName.trim() && <div style={{ fontSize: 11, color: '#c5221f', marginTop: 3 }}>Required</div>}
                    </div>

                    <div className="form-group" style={{ marginTop: 12 }}>
                      <label className="form-label">Area (Acres)</label>
                      <input className="form-control" type="number" step="0.01" min="0" placeholder="e.g. 1.25"
                        value={pondForm.area}
                        onChange={e => setPondForm(f => ({ ...f, area: e.target.value }))} />
                      <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>Optional — decimals allowed</div>
                    </div>

                    <div className="form-group" style={{ marginTop: 12 }}>
                      <label className="form-label">Description</label>
                      <textarea className="form-control" rows={3}
                        placeholder="e.g. North-east corner, 1.5 acres, fully lined"
                        value={pondForm.description}
                        onChange={e => setPondForm(f => ({ ...f, description: e.target.value }))} />
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                      <button className="btn btn-primary" onClick={handlePondSave}
                        disabled={pondSaving || !pondForm.pondName.trim()}
                        style={{ opacity: pondForm.pondName.trim() ? 1 : 0.45 }}>
                        {pondSaving ? 'Saving…' : pondEditId ? 'Update Pond' : 'Add Pond'}
                      </button>
                      {pondEditId && (
                        <button className="btn btn-secondary" onClick={() => { setPondForm({ ...EMPTY_POND }); setPondEditId(null); setPondErr(''); }}>
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
          </div>

        </div>
      )}

      {/* ── Release Dates tab ────────────────────────────────────────────────── */}
      {tab === 'release' && (() => {
        const selectedCrop = crops.find(c => String(c.id) === String(relCropId));

        return (
          <div>
            {/* Crop selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              background: '#f8f9fa', borderRadius: 8, border: '1px solid #e8eaed', marginBottom: 20 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#5f6368', whiteSpace: 'nowrap' }}>🦐 Select Crop:</span>
              <select className="form-control" style={{ width: 240, padding: '5px 8px' }}
                value={relCropId} onChange={e => handleRelCropChange(e.target.value)}>
                <option value="">— Choose a crop —</option>
                {crops.map(c => <option key={c.id} value={c.id}>{c.crop_code} ({c.status})</option>)}
              </select>
              {selectedCrop && (
                <span style={{ fontSize: 12, color: '#888' }}>
                  {ponds.length} farm pond{ponds.length !== 1 ? 's' : ''} · Started {fmtDate(selectedCrop.start_date)}
                </span>
              )}
            </div>

            {!relCropId && (
              <div className="card">
                <div className="card-body" style={{ textAlign: 'center', color: '#888', padding: 48 }}>
                  Select a crop above to manage its pond release dates
                </div>
              </div>
            )}

            {relCropId && relLoading && <div className="loading-msg">Loading release dates…</div>}

            {relCropId && !relLoading && (
              <div className="card">
                <div className="card-header">
                  <div className="card-title">🐟 Pond Stocking — {selectedCrop?.crop_code}</div>
                  <span style={{ fontSize: 12, color: '#888' }}>Seed stocking records per crop &amp; pond</span>
                </div>
                <div className="card-body">
                  {relErr && <div className="alert alert-error" style={{ marginBottom: 14 }}>{relErr}</div>}

                  {ponds.length === 0 && (
                    <div style={{ color: '#e37400', fontSize: 13, marginBottom: 12, padding: '10px 14px',
                      background: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082' }}>
                      ⚠️ No ponds defined for this farm — go to the <strong>🏊 Ponds</strong> tab and add your farm ponds first.
                    </div>
                  )}

                  {ponds.length > 0 && (() => {
                    const pd       = relData[relSelectedPond] || {};
                    const saved    = relRows.find(r => r.pond_no === relSelectedPond);
                    const setField = (field, val) => setRelData(prev => ({
                      ...prev, [relSelectedPond]: { ...(prev[relSelectedPond] || {}), [field]: val }
                    }));
                    const seedCost = pd.seedCount && pd.seedRate
                      ? parseFloat(pd.seedCount) * parseFloat(pd.seedRate) : 0;
                    const canSave  = !!relSelectedPond && !!pd.date;

                    return (
                      <div style={{ maxWidth: 480 }}>

                        {/* Pond selector */}
                        <div className="form-group" style={{ marginBottom: 14 }}>
                          <label className="form-label">Pond <span style={{ color: '#c5221f' }}>*</span></label>
                          <select className="form-control" value={relSelectedPond}
                            onChange={e => { setRelSelectedPond(e.target.value); setRelErr(''); }}
                            style={!relSelectedPond ? {} : {}}>
                            <option value="">— Select pond —</option>
                            {ponds.map(p => {
                              const hasSaved = relRows.some(r => r.pond_no === p.pond_name);
                              return <option key={p.id} value={p.pond_name}>{p.pond_name}{hasSaved ? ' ✓' : ''}</option>;
                            })}
                          </select>
                        </div>

                        {/* Release Date */}
                        <div className="form-group" style={{ marginBottom: 14 }}>
                          <label className="form-label">Release Date <span style={{ color: '#c5221f' }}>*</span></label>
                          <DateInput className="form-control" value={pd.date || ''}
                            onChange={e => setField('date', e.target.value)}
                            style={relSelectedPond && !pd.date ? {} : {}} />
                          {saved?.release_date && (
                            <div style={{ fontSize: 11, color: '#137333', marginTop: 3 }}>
                              Saved: {fmtDate(saved.release_date)}
                            </div>
                          )}
                        </div>

                        {/* Seed Count */}
                        <div className="form-group" style={{ marginBottom: 14 }}>
                          <label className="form-label">Seed Count (PCs)</label>
                          <input className="form-control" type="number" step="1" min="1" placeholder="e.g. 100000"
                            value={pd.seedCount || ''}
                            onChange={e => setField('seedCount', e.target.value.replace('.', ''))} />
                        </div>

                        {/* Seed Rate */}
                        <div className="form-group" style={{ marginBottom: 14 }}>
                          <label className="form-label">Seed Rate (₹/pc)</label>
                          <input className="form-control" type="number" step="0.0001" min="0" placeholder="e.g. 0.35"
                            value={pd.seedRate || ''}
                            onChange={e => setField('seedRate', e.target.value)} />
                          {seedCost > 0 && (
                            <div style={{ fontSize: 12, color: '#137333', marginTop: 4, fontWeight: 600 }}>
                              Seed Cost: ₹{seedCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          )}
                        </div>

                        {/* Supplier */}
                        <div className="form-group" style={{ marginBottom: 14 }}>
                          <label className="form-label">Supplier</label>
                          <input className="form-control" placeholder="e.g. ABC Hatchery"
                            value={pd.supplier || ''}
                            onChange={e => setField('supplier', e.target.value)} />
                        </div>

                        {/* Notes */}
                        <div className="form-group" style={{ marginBottom: 16 }}>
                          <label className="form-label">Notes</label>
                          <textarea className="form-control" rows={3}
                            value={pd.notes || ''}
                            onChange={e => setField('notes', e.target.value)} />
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          {canWrite && (
                            <button className="btn btn-primary" onClick={handleRelSave}
                              disabled={relSaving || !canSave}
                              style={{ opacity: canSave ? 1 : 0.45, cursor: canSave ? 'pointer' : 'not-allowed' }}>
                              {relSaving ? 'Saving…' : '💾 Save Stocking'}
                            </button>
                          )}
                          {saved && canDelete && (
                            <button className="btn btn-danger" onClick={() => handleRelDelete(saved.id)}>
                              Clear Saved
                            </button>
                          )}
                          {!canSave && <span style={{ fontSize: 12, color: '#888' }}>Select a pond and enter a release date</span>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Summary of saved records */}
                  {relRows.length > 0 && (
                    <div style={{ marginTop: 24, borderTop: '1px solid #e8eaed', paddingTop: 18 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#3c4043', marginBottom: 10 }}>✅ Saved Release / Stocking Records</div>
                      <div style={{ overflowX: 'auto' }}>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Pond</th>
                              <th>Release Date</th>
                              <th style={{ textAlign: 'right' }}>Seed Count</th>
                              <th style={{ textAlign: 'right' }}>Rate (₹/pc)</th>
                              <th style={{ textAlign: 'right' }}>Seed Cost</th>
                              <th>Supplier</th>
                              {canDelete && <th>Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {relRows.map(r => {
                              const cost = (parseFloat(r.seed_count || 0) * parseFloat(r.seed_rate || 0));
                              return (
                                <tr key={r.id}>
                                  <td><span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '2px 10px', fontWeight: 700 }}>{r.pond_no}</span></td>
                                  <td style={{ fontWeight: 600 }}>{r.release_date ? fmtDate(r.release_date) : '—'}</td>
                                  <td style={{ textAlign: 'right' }}>{r.seed_count ? Number(r.seed_count).toLocaleString('en-IN') : '—'}</td>
                                  <td style={{ textAlign: 'right' }}>{r.seed_rate ? `₹${parseFloat(r.seed_rate).toFixed(4)}` : '—'}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600, color: '#137333' }}>
                                    {cost > 0 ? `₹${cost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                  </td>
                                  <td style={{ color: '#5f6368', fontSize: 13 }}>{r.supplier || '—'}</td>
                                  {canDelete && (
                                    <td>
                                      <button className="btn-sm btn-danger" onClick={() => handleRelDelete(r.id)}>Clear</button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                          {relRows.length > 0 && relRows.some(r => r.seed_count) && (
                            <tfoot>
                              <tr>
                                <td colSpan={2} style={{ fontWeight: 700, padding: '10px 12px' }}>Total</td>
                                <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>
                                  {Number(relRows.reduce((s, r) => s + (parseInt(r.seed_count) || 0), 0)).toLocaleString('en-IN')}
                                </td>
                                <td></td>
                                <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#137333' }}>
                                  {(() => {
                                    const t = relRows.reduce((s, r) => s + (parseFloat(r.seed_count || 0) * parseFloat(r.seed_rate || 0)), 0);
                                    return t > 0 ? `₹${t.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
                                  })()}
                                </td>
                                <td colSpan={canDelete ? 2 : 1}></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
